import { Component, Injector, Input, OnInit } from '@angular/core';
import {
  AbstractComponent,
  AttendanceCopilotActionsService,
  CopilotActionsItem,
  CopilotActionsItemState,
  CopilotActionsUiState,
  CopilotActionsView,
  CopilotComposeBridgeService,
} from 'lib-trend-core';
import { Observable, takeUntil } from 'rxjs';
import { isToday, timeLabel } from '../attendance-copilot-insight/insight-time.util';

const KEYS = 'attendance.copilot.actions.';

/**
 * The Actions pane of the Copilot drawer (SPEC-2026-10-copilot-attendance-actions, RF-33 to
 * RF-38, plan AD-10; prototype D): every pending action with its details, then the done and
 * dismissed ones, collapsed. The state lives in `AttendanceCopilotActionsService`; who may mark
 * and what is listed are the API's answer.
 */
@Component({
  selector: 'attendance-copilot-actions',
  templateUrl: './attendance-copilot-actions.component.html',
  styleUrls: ['./attendance-copilot-actions.component.scss'],
  standalone: false,
})
export class AttendanceCopilotActionsComponent extends AbstractComponent implements OnInit {
  @Input() attendanceClosed = false;

  readonly state$: Observable<CopilotActionsUiState> = this.actionsService.select();
  /** The done and dismissed actions open on request only (D-10). */
  markedOpen = false;
  timezone = '';

  private openedId: string | null = null;

  constructor(
    injector: Injector,
    private readonly actionsService: AttendanceCopilotActionsService,
    private readonly composeBridge: CopilotComposeBridgeService,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.timezone = this.getTimezoneCompanyCurrentUser();
    this.state$
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => this.resetOnNewConversation(state.idAttendance));
  }

  /** The actions are generated on request when the insight predates them (RF-33, spec M-2). */
  notGenerated(view: CopilotActionsView): boolean {
    return view.canGenerate && !this.hasActions(view) && !this.closed(view);
  }

  closed(view: CopilotActionsView): boolean {
    return this.attendanceClosed || view.readOnly;
  }

  hasActions(view: CopilotActionsView): boolean {
    return !!view.actions?.items.length;
  }

  pending(view: CopilotActionsView): CopilotActionsItem[] {
    return (view.actions?.items ?? []).filter((item) => item.state === 'pending');
  }

  marked(view: CopilotActionsView): CopilotActionsItem[] {
    return (view.actions?.items ?? []).filter((item) => item.state !== 'pending');
  }

  /** A generation in progress; a closed conversation leaves that state (RF-08). */
  generating(view: CopilotActionsView): boolean {
    return view.state === 'generating' && !this.closed(view);
  }

  failed(view: CopilotActionsView): boolean {
    return view.state === 'failed' && !this.closed(view);
  }

  /** The controls that mark are offered to the responsible attendant only (RF-35). */
  markable(view: CopilotActionsView): boolean {
    return view.canMark && !this.closed(view);
  }

  isMarking(state: CopilotActionsUiState, item: CopilotActionsItem): boolean {
    return state.marking.includes(item.id);
  }

  generatedKey(generatedAt: string): string {
    return KEYS + (isToday(generatedAt, this.timezone) ? 'generatedToday' : 'generatedOn');
  }

  progressKey(view: CopilotActionsView): string {
    return KEYS + (this.hasActions(view) ? 'updating' : 'generating');
  }

  failedKey(view: CopilotActionsView): string {
    return KEYS + (this.hasActions(view) ? 'updateFailed' : 'generateFailed');
  }

  /** The time of an evidence or a mark, with its date when it is not from today (RF-14, RF-34). */
  when(at?: string): string {
    return at ? timeLabel(at, this.timezone) : '';
  }

  textId(item: CopilotActionsItem): string {
    return `copilot-action-text-${item.id}`;
  }

  toggleMarked(): void {
    this.markedOpen = !this.markedOpen;
  }

  mark(item: CopilotActionsItem, state: CopilotActionsItemState): void {
    this.actionsService.mark(item.id, state);
  }

  generate(): void {
    this.actionsService.generate();
  }

  /** Places the ready text in the reply field as the reply suggestion does: it sends nothing and marks nothing (RF-31, RF-36). */
  useText(item: CopilotActionsItem): void {
    if (item.readyText) this.composeBridge.pushToComposer(item.readyText);
  }

  private resetOnNewConversation(idAttendance: string | null): void {
    if (idAttendance === this.openedId) return;
    this.openedId = idAttendance;
    this.markedOpen = false;
  }
}
